/**
 * Customizes the font-weight property in the GrapesJS Studio SDK style manager
 * to use numeric values (400, 500, 600, 700, 800) instead of text labels.
 *
 * @param editor - The GrapesJS editor instance
 */
export const customizeFontWeightProperty = (editor: any): void => {
  const styleManager = editor.StyleManager || editor.get('StyleManager');
  if (!styleManager) {
    return;
  }

  // Get the typography sector
  const typographySector = styleManager.getSector?.('gs-typography');

  if (!typographySector) {
    return;
  }

  const properties = typographySector.getProperties?.() || typographySector.properties || [];
  const fontWeightProp = properties.find((prop: any) => {
    const propName = prop.get?.('property') || prop.get?.('name') || prop.property || prop.name;
    return propName === 'font-weight' || propName === 'fontWeight';
  });

  if (!fontWeightProp) {
    return;
  }

  // Update options to use numeric values
  const numericOptions = [
    { value: '400', name: '400' },
    { value: '500', name: '500' },
    { value: '600', name: '600' },
    { value: '700', name: '700' },
    { value: '800', name: '800' },
  ];

  // Try removing and re-adding the property
  const propIndex = properties.indexOf(fontWeightProp);
  if (propIndex !== -1 && typographySector.removeProperty) {
    typographySector.removeProperty(fontWeightProp);
    typographySector.addProperty(
      'gs-typography',
      {
        name: 'Font Weight',
        property: 'font-weight',
        type: 'select',
        defaults: '400',
        options: numericOptions,
      },
      { at: propIndex }
    );
  } else {
    // Try multiple methods to set options
    if (fontWeightProp.set) {
      fontWeightProp.set('options', numericOptions);
    }
    if (fontWeightProp.setOptions) {
      fontWeightProp.setOptions(numericOptions);
    }
    if (fontWeightProp.update) {
      fontWeightProp.update({ options: numericOptions });
    }
    // Direct assignment as fallback
    fontWeightProp.options = numericOptions;
    if (fontWeightProp.set) {
      fontWeightProp.set('options', numericOptions);
    }
  }
};

/**
 * Sets up font-weight customization with retry logic to ensure
 * the style manager is ready before customizing.
 *
 * @param editor - The GrapesJS editor instance
 */
export const setupFontWeightCustomization = (editor: any): void => {
  if (!editor) {
    return;
  }

  // Try immediately and then retry up to 15 times with 200ms delay between each attempt
  const maxRetries = 15;
  const retryDelay = 200;

  // Try immediately
  customizeFontWeightProperty(editor);

  // Schedule retries
  if (typeof window !== 'undefined') {
    for (let i = 1; i <= maxRetries; i++) {
      window.setTimeout(() => customizeFontWeightProperty(editor), i * retryDelay);
    }
  }
};
